import React, { useState, useEffect } from 'react';
import { trainingsApi, usersApi, courseRequestsApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Chalkboard, UserPlus, CalendarBlank, Users, CaretLeft, CaretRight, Warning, CalendarPlus, CaretDown, CaretUp, ListChecks, CheckCircle, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;
const MAX_CONTRACTOR_SLOTS = 5;

const AssignedBadge = ({ info, workerId, formatDay }) => (
  info.status === 'inscrito' ? (
    <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs" data-testid={`assigned-badge-${workerId}`}>
      <CheckCircle size={12} className="mr-1" /> Asignado · {formatDay(info.scheduled_date)}
    </Badge>
  ) : (
    <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-xs" data-testid={`assigned-badge-${workerId}`}>
      <Clock size={12} className="mr-1" /> Solicitud pendiente{info.preferred_date ? ` · ${info.preferred_date}` : ''}
    </Badge>
  )
);

export default function ContractorTrainingsPage() {
  const [courses, setCourses] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [eligibilityMap, setEligibilityMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [preferredMode, setPreferredMode] = useState(false);
  const [preferredDate, setPreferredDate] = useState('');
  const [eapCourses, setEapCourses] = useState([]);
  const [expandedWorker, setExpandedWorker] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, workersRes, eapRes] = await Promise.all([
        courseRequestsApi.getAvailableCourses(),
        usersApi.getAll({ role: 'worker', page_size: 999 }),
        courseRequestsApi.getEapCourses()
      ]);
      setCourses(coursesRes.data || []);
      setWorkers(workersRes.data.items || workersRes.data);
      setEapCourses(eapRes.data || []);
      return coursesRes.data || [];
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const remainingSlots = selectedCourse?.remaining_slots ?? MAX_CONTRACTOR_SLOTS;

  const openDialog = async (course) => {
    setSelectedCourse(course);
    setSelectedWorkers([]);
    setEligibilityMap({});
    setPreferredMode(false);
    setPreferredDate('');
    setExpandedWorker(null);
    setDialogOpen(true);
    try {
      const eligRes = await trainingsApi.checkEligibility(
        course.suggested_training.id,
        workers.map(w => w.id)
      );
      setEligibilityMap(eligRes.data || {});
    } catch {
      setEligibilityMap({});
    }
  };

  const toggleWorker = (workerId) => {
    setSelectedWorkers(prev => {
      if (prev.includes(workerId)) return prev.filter(id => id !== workerId);
      if (prev.length >= remainingSlots) {
        toast.error(`Máximo ${remainingSlots} cupo(s) disponible(s) para tu empresa en esta capacitación.`);
        return prev;
      }
      return [...prev, workerId];
    });
  };

  const handleAccept = async () => {
    if (selectedWorkers.length === 0) { toast.error('Selecciona al menos un trabajador'); return; }
    setSubmitting(true);
    try {
      const res = await courseRequestsApi.create({
        course_id: selectedCourse.course_id,
        worker_ids: selectedWorkers,
        mode: 'accept',
        training_id: selectedCourse.suggested_training.id,
      });
      toast.success(res.data.message || 'Trabajadores asignados');
      setSelectedWorkers([]);
      const fresh = await fetchData();
      const updated = (fresh || []).find(c => c.course_id === selectedCourse.course_id);
      if (updated && updated.suggested_training.id === selectedCourse.suggested_training.id) setSelectedCourse(updated);
      else setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreferred = async () => {
    if (selectedWorkers.length === 0) { toast.error('Selecciona al menos un trabajador'); return; }
    if (!preferredDate.trim()) { toast.error('Indica el día que te acomoda'); return; }
    setSubmitting(true);
    try {
      const res = await courseRequestsApi.create({
        course_id: selectedCourse.course_id,
        worker_ids: selectedWorkers,
        mode: 'preferred',
        preferred_date: preferredDate.trim(),
      });
      toast.success(res.data.message || 'Solicitud enviada');
      setSelectedWorkers([]);
      setPreferredMode(false);
      const fresh = await fetchData();
      const updated = (fresh || []).find(c => c.course_id === selectedCourse.course_id);
      if (updated) setSelectedCourse(updated); else setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const assignedMap = Object.fromEntries((selectedCourse?.assigned || []).map(a => [a.worker_id, a]));
  const sortedWorkers = [...workers].sort((a, b) => (assignedMap[a.id] ? 1 : 0) - (assignedMap[b.id] ? 1 : 0));
  const formatDay = (iso) => new Date(iso).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' });

  const totalPages = Math.ceil(courses.length / ITEMS_PER_PAGE);
  const paginated = courses.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const opts = { timeZone: 'America/Santiago' };
    return `${d.toLocaleDateString('es-CL', opts)} ${d.toLocaleTimeString('es-CL', { ...opts, hour: '2-digit', minute: '2-digit' })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractor-trainings-page">
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Cursos Disponibles</h1>
        <p className="text-[#2B4B8C]/60">Solicita asignar a tus trabajadores a un curso</p>
      </div>

      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                <TableHead className="text-[#2B4B8C]/60">Curso</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Cupos</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map((course) => (
                <TableRow key={course.course_id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2BBAA4]/10 flex items-center justify-center shrink-0">
                        <Chalkboard size={20} className="text-[#2BBAA4]" />
                      </div>
                      <div>
                        <p className="text-[#2B4B8C] font-medium">{course.course_name}</p>
                        {course.assigned?.length > 0 && (
                          <p className="text-xs text-blue-600 mt-0.5" data-testid={`assigned-count-${course.course_id}`}>
                            {course.assigned.length} trabajador(es) ya asignado(s)
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-[#2B4B8C]/60">
                      <Users size={14} />
                      <span>{course.remaining_slots} de {MAX_CONTRACTOR_SLOTS} por empresa</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => openDialog(course)}
                      className="bg-[#2BBAA4] text-black hover:bg-[#239986] text-xs"
                      data-testid={`request-btn-${course.course_id}`}
                    >
                      <UserPlus size={14} className="mr-1" />
                      Solicitar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {courses.length === 0 && (
            <div className="text-center py-12 text-[#2B4B8C]/50">
              No hay cursos con capacitaciones disponibles
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#2B4B8C]/10">
              <span className="text-sm text-[#2B4B8C]/60">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, courses.length)} de {courses.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                  <CaretLeft size={16} className="mr-1" /> Anterior
                </Button>
                <span className="text-sm text-[#2B4B8C] font-medium px-2">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                  Siguiente <CaretRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C]">
              Solicitar - {selectedCourse?.course_name}
            </DialogTitle>
          </DialogHeader>
          {selectedCourse && (
            <div className="space-y-4">
              {/* Suggested date */}
              <div className="rounded-lg bg-[#2BBAA4]/5 border border-[#2BBAA4]/20 p-3" data-testid="suggested-training-box">
                <p className="text-xs text-[#2B4B8C]/60 mb-1">Capacitación más próxima sugerida</p>
                <div className="flex items-center gap-2 text-[#2B4B8C] font-medium">
                  <CalendarBlank size={16} className="text-[#2BBAA4]" />
                  {formatDate(selectedCourse.suggested_training.scheduled_date)}
                  <Badge className="bg-[#2B4B8C]/10 text-[#2B4B8C] border-[#2B4B8C]/20 border text-xs ml-2">
                    {selectedCourse.suggested_training.modality || '-'}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-[#2B4B8C]/60">
                  Selecciona los trabajadores ({selectedWorkers.length} seleccionados)
                </p>
                <Badge className="bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/20 border text-xs">
                  {remainingSlots} cupo(s) disponible(s)
                </Badge>
              </div>

              <div className="rounded-lg border border-[#2B4B8C]/10 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="text-[#2B4B8C]/60 text-xs">Trabajador</TableHead>
                      <TableHead className="text-[#2B4B8C]/60 text-xs">RUT</TableHead>
                      <TableHead className="text-[#2B4B8C]/60 text-xs">Habilitado</TableHead>
                      <TableHead className="text-[#2B4B8C]/60 text-xs text-right pr-3">EAP y RIC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedWorkers.map(worker => {
                      const assignedInfo = assignedMap[worker.id];
                      const elig = eligibilityMap[worker.id];
                      const blocked = !!assignedInfo || (elig && !elig.eligible);
                      const reasons = elig?.reasons || [];
                      const expanded = expandedWorker === worker.id;
                      const suggestedDay = selectedCourse?.suggested_training?.scheduled_date
                        ? new Date(selectedCourse.suggested_training.scheduled_date).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })
                        : null;
                      return (
                        <React.Fragment key={worker.id}>
                        <TableRow
                          className={`${assignedInfo ? 'bg-blue-50/50' : blocked ? 'bg-red-50/40' : 'hover:bg-[#2BBAA4]/5 cursor-pointer'} border-[#2B4B8C]/5`}
                          data-testid={`dialog-worker-row-${worker.id}`}
                          onClick={() => !blocked && toggleWorker(worker.id)}
                        >
                          <TableCell className="py-2 pl-3">
                            <Checkbox checked={selectedWorkers.includes(worker.id)} disabled={blocked} />
                          </TableCell>
                          <TableCell className="py-2">
                            <p className="text-sm text-[#2B4B8C] font-medium">{worker.first_name} {worker.last_name}</p>
                            <p className="text-xs text-[#2B4B8C]/50">{worker.email}</p>
                          </TableCell>
                          <TableCell className="py-2 text-sm text-[#2B4B8C]/60">{worker.rut || '-'}</TableCell>
                          <TableCell className="py-2">
                            {assignedInfo ? (
                              <AssignedBadge info={assignedInfo} workerId={worker.id} formatDay={formatDay} />
                            ) : blocked ? (
                              <div className="flex items-start gap-1.5">
                                <Warning size={14} className="text-red-500 shrink-0 mt-0.5" />
                                <div className="flex flex-col gap-0.5">
                                  {reasons.map((r, i) => (
                                    <span key={i} className="text-xs text-red-500 whitespace-normal break-words">{r.replace(/^R\d: /, '')}</span>
                                  ))}
                                </div>
                              </div>
                            ) : elig ? (
                              <Badge className="bg-green-500/10 text-green-600 border-green-500/20 border text-xs">Habilitado</Badge>
                            ) : (
                              <span className="text-xs text-[#2B4B8C]/30">Verificando...</span>
                            )}
                          </TableCell>
                          <TableCell className="py-2 text-right pr-3">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setExpandedWorker(expanded ? null : worker.id); }}
                              className="inline-flex items-center gap-1 text-xs text-[#2B4B8C]/70 hover:text-[#2B4B8C]"
                              data-testid={`eap-toggle-${worker.id}`}
                            >
                              <ListChecks size={14} /> EAP y RIC {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
                            </button>
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow className="bg-[#f8fafc] border-[#2B4B8C]/5" data-testid={`eap-panel-${worker.id}`}>
                            <TableCell colSpan={5} className="py-3 px-4">
                              <p className="text-xs font-semibold text-[#2B4B8C] mb-2">Capacitaciones a coordinar el mismo día (EAP y Reglamento Interno de Conducción)</p>
                              <div className="space-y-1.5">
                                {eapCourses.length === 0 && (
                                  <p className="text-xs text-[#2B4B8C]/50">No hay cursos EAP configurados.</p>
                                )}
                                {eapCourses.map(eap => {
                                  const nt = eap.nearest_training;
                                  const ntDay = nt ? new Date(nt.scheduled_date).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' }) : null;
                                  const sameDay = ntDay && suggestedDay && ntDay === suggestedDay;
                                  return (
                                    <div key={eap.course_id} className="flex items-center justify-between text-xs">
                                      <span className="text-[#2B4B8C]/80">{eap.course_name}</span>
                                      {nt ? (
                                        <span className={`inline-flex items-center gap-1 ${sameDay ? 'text-green-600 font-medium' : 'text-[#2B4B8C]/60'}`}>
                                          <CalendarBlank size={12} /> {formatDate(nt.scheduled_date)}
                                          {sameDay && <Badge className="bg-green-500/10 text-green-600 border-green-500/20 border text-[10px] ml-1">mismo día</Badge>}
                                        </span>
                                      ) : (
                                        <span className="text-[#2B4B8C]/30">Sin fecha próxima</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {workers.length === 0 && (
                  <p className="text-sm text-[#2B4B8C]/50 text-center py-6">No hay trabajadores registrados</p>
                )}
              </div>

              {/* Preferred date section */}
              {preferredMode && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="preferred-date-box">
                  <p className="text-sm text-[#2B4B8C]/70">Escribe el día que les acomoda:</p>
                  <Input
                    value={preferredDate}
                    onChange={(e) => setPreferredDate(e.target.value)}
                    placeholder="Ej: martes 24 de marzo"
                    className="border-[#2B4B8C]/15"
                    data-testid="preferred-date-input"
                  />
                  <p className="text-xs text-[#2B4B8C]/50">El administrador gestionará la solicitud según el día indicado.</p>
                </div>
              )}

              {/* Actions */}
              {!preferredMode ? (
                <div className="flex flex-col gap-2 pt-4 border-t border-[#2B4B8C]/10">
                  <Button
                    onClick={handleAccept}
                    disabled={selectedWorkers.length === 0 || submitting}
                    className="w-full bg-[#2BBAA4] text-black hover:bg-[#239986]"
                    data-testid="accept-suggested-btn"
                  >
                    {submitting ? 'Asignando...' : `Aceptar fecha sugerida y asignar (${selectedWorkers.length})`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPreferredMode(true)}
                    className="w-full border-[#2B4B8C]/15 text-[#2B4B8C]"
                    data-testid="propose-date-btn"
                  >
                    <CalendarPlus size={16} className="mr-1" /> La fecha no me acomoda
                  </Button>
                </div>
              ) : (
                <div className="flex gap-3 pt-4 border-t border-[#2B4B8C]/10">
                  <Button
                    variant="outline"
                    onClick={() => setPreferredMode(false)}
                    className="flex-1 border-[#2B4B8C]/15 text-[#2B4B8C]"
                    data-testid="back-to-suggested-btn"
                  >
                    Volver
                  </Button>
                  <Button
                    onClick={handlePreferred}
                    disabled={selectedWorkers.length === 0 || submitting || !preferredDate.trim()}
                    className="flex-1 bg-[#2B4B8C] text-white hover:bg-[#233d75]"
                    data-testid="submit-preferred-btn"
                  >
                    {submitting ? 'Enviando...' : 'Enviar solicitud'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
